// Toice Risk Intelligence - Proprietary Risk Scoring Engine

const SEVERITY_WEIGHTS = {
  'Pest Issue': 75,
  'Safety Issue': 95,
  'Structural Issue': 80,
  'Plumbing': 70,
  'Heating/Cooling': 75,
  'Maintenance': 50,
  'Security Concern': 60,
  'Unauthorized Entry': 65,
  'Trip Hazard': 55,
  'Noise': 30,
  'Mold': 100,
  'Moisture': 95,
  'Unsafe Balcony': 95
};

export const calculateBuildingRiskScore = (building) => {
  const { reviews, improvements, numberOfUnits } = building;

  if (!reviews || reviews.length === 0) {
    return {
      riskScore: 0,
      totalIssues: 0,
      outstandingIssues: 0,
      resolvedIssues: 0,
      criticalIssues: 0,
      riskLevel: 'LOW',
      issuesPerUnit: 0
    };
  }

  // Count approved improvements to determine resolved issues
  const approvedImprovements = improvements ?
    improvements.filter(imp => imp.status === 'approved' || imp.status === 'completed') : [];

  // Simple heuristic: each approved improvement resolves ~1 issue
  const resolvedIssuesCount = Math.min(approvedImprovements.length, reviews.length);
  const outstandingIssuesCount = reviews.length - resolvedIssuesCount;

  // Calculate average remediation time (in days)
  let avgRemediationTime = 'N/A';
  if (approvedImprovements.length > 0) {
    const improvementsWithTime = approvedImprovements.filter(imp => imp.remediationDays);
    if (improvementsWithTime.length > 0) {
      const totalDays = improvementsWithTime.reduce((sum, imp) => sum + imp.remediationDays, 0);
      avgRemediationTime = Math.round(totalDays / improvementsWithTime.length);
    }
  }

  let totalRisk = 0;
  let criticalCount = 0;

  reviews.forEach(review => {
    const baseScore = SEVERITY_WEIGHTS[review.category] || 40;

    // Factor in 5-star rating
    const rating = review.rating || 3;
    let ratingMultiplier = 1.0;

    if (rating === 1) ratingMultiplier = 2.0;
    else if (rating === 2) ratingMultiplier = 1.5;
    else if (rating === 4) ratingMultiplier = 0.7;
    else if (rating === 5) ratingMultiplier = 0.5;

    const adjustedScore = baseScore * ratingMultiplier;
    totalRisk += adjustedScore;

    if (baseScore >= 80 || rating === 1) {
      criticalCount++;
    }
  });

  let riskScore = totalRisk / reviews.length;
  
  // BUILDING SIZE ADJUSTMENT
  // Issues per unit is a critical metric
  if (numberOfUnits && numberOfUnits > 0) {
    const issuesPerUnit = reviews.length / numberOfUnits;
    
    // Scale risk based on issues per unit ratio
    // More issues per unit = worse building condition
    if (issuesPerUnit > 0.5) {
      // More than 1 issue per 2 units = serious problem
      riskScore *= 1.5;
    } else if (issuesPerUnit > 0.3) {
      // More than 1 issue per 3 units = moderate concern
      riskScore *= 1.25;
    } else if (issuesPerUnit > 0.15) {
      // More than 1 issue per 7 units = slight concern
      riskScore *= 1.1;
    } else if (issuesPerUnit < 0.05) {
      // Less than 1 issue per 20 units = well-maintained
      riskScore *= 0.85;
    }
  } else {
    // If no unit count, assume first reviewer's estimate or use default
    // Default assumption: 20 units for unknown buildings
    const estimatedUnits = 20;
    const issuesPerUnit = reviews.length / estimatedUnits;
    
    if (issuesPerUnit > 0.5) {
      riskScore *= 1.3;
    } else if (issuesPerUnit < 0.1) {
      riskScore *= 0.9;
    }
  }
  
  // Reduce score for approved improvements
  if (approvedImprovements && approvedImprovements.length > 0) {
    riskScore -= (approvedImprovements.length * 10);
  }

  riskScore = Math.min(100, Math.max(0, riskScore));

  let riskLevel = 'LOW';
  if (riskScore >= 80) riskLevel = 'CRITICAL';
  else if (riskScore >= 60) riskLevel = 'HIGH';
  else if (riskScore >= 40) riskLevel = 'MODERATE';

  return {
    riskScore: Math.round(riskScore),
    totalIssues: reviews.length,
    outstandingIssues: outstandingIssuesCount,
    resolvedIssues: resolvedIssuesCount,
    criticalIssues: criticalCount,
    riskLevel,
    issuesPerUnit: numberOfUnits ? (reviews.length / numberOfUnits).toFixed(3) : 'Unknown',
    outstandingIssuesPerUnit: numberOfUnits ? (outstandingIssuesCount / numberOfUnits).toFixed(3) : 'Unknown',
    numberOfUnits: numberOfUnits || 'Not specified',
    avgRemediationTime: avgRemediationTime
  };
};

// Named export only - no default export needed